import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate } from "@/lib/utils";
import { Trash2 } from "lucide-react";

export const IssueReportsPanel = ({ reports, loading, language, onDeleteReport }) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deletingReportId, setDeletingReportId] = useState(null);

  const text = language === "es" ? {
    delete: "Eliminar",
    deleteTitle: "Eliminar reporte",
    deleteDescription: "Esta acción borrará el reporte de forma permanente de MongoDB.",
    cancel: "Cancelar",
    confirm: "Sí, eliminar",
    deleting: "Eliminando...",
  } : {
    delete: "Delete",
    deleteTitle: "Delete report",
    deleteDescription: "This action will permanently remove the issue report from MongoDB.",
    cancel: "Cancel",
    confirm: "Yes, delete",
    deleting: "Deleting...",
  };

  const handleDelete = async () => {
    if (!reportToDelete || !onDeleteReport) {
      return;
    }

    try {
      setDeletingReportId(reportToDelete.report_id);
      await onDeleteReport(reportToDelete.report_id);
      if (selectedReport?.report_id === reportToDelete.report_id) {
        setSelectedReport(null);
      }
      setReportToDelete(null);
    } finally {
      setDeletingReportId(null);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border p-8 text-center text-muted-foreground" data-testid="admin-issue-reports-loading">
        {language === "es" ? "Cargando reportes..." : "Loading reports..."}
      </Card>
    );
  }

  if (!reports.length) {
    return (
      <Card className="bg-card border-border p-8 text-center text-muted-foreground" data-testid="admin-issue-reports-empty-state">
        {language === "es" ? "Todavía no hay reportes enviados." : "No issue reports yet."}
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card border-border overflow-hidden" data-testid="admin-issue-reports-panel">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{language === "es" ? "Fecha" : "Date"}</TableHead>
              <TableHead>{language === "es" ? "Usuario" : "User"}</TableHead>
              <TableHead>{language === "es" ? "Mensaje" : "Message"}</TableHead>
              <TableHead>{language === "es" ? "Origen" : "Source"}</TableHead>
              <TableHead>{language === "es" ? "Email" : "Email"}</TableHead>
              <TableHead className="text-right">{language === "es" ? "Acciones" : "Actions"}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((report) => (
              <TableRow key={report.report_id} data-testid={`admin-issue-report-row-${report.report_id}`}>
                <TableCell className="text-sm text-muted-foreground">{formatDate(report.created_at)}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium" data-testid={`admin-issue-report-user-${report.report_id}`}>{report.user_username}</p>
                    <p className="text-xs text-muted-foreground">{report.user_email}</p>
                  </div>
                </TableCell>
                <TableCell className="max-w-md">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto w-full justify-start px-0 text-left"
                    onClick={() => setSelectedReport(report)}
                    data-testid={`admin-issue-report-message-button-${report.report_id}`}
                  >
                    <span className="line-clamp-3 whitespace-pre-wrap text-sm" data-testid={`admin-issue-report-message-${report.report_id}`}>
                      {report.message}
                    </span>
                  </Button>
                </TableCell>
                <TableCell>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p data-testid={`admin-issue-report-path-${report.report_id}`}>{report.page_path || "—"}</p>
                    <p data-testid={`admin-issue-report-video-${report.report_id}`}>{report.video_filename || report.video_id || "—"}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={report.email_notification_sent ? "border-green-500/30 text-green-400" : "border-amber-500/30 text-amber-400"}
                    data-testid={`admin-issue-report-email-status-${report.report_id}`}
                  >
                    {report.email_notification_sent
                      ? (language === "es" ? "Enviado" : "Sent")
                      : (language === "es" ? "Solo inbox" : "Inbox only")}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setReportToDelete(report)}
                    disabled={deletingReportId === report.report_id}
                    data-testid={`admin-issue-report-delete-button-${report.report_id}`}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {deletingReportId === report.report_id ? text.deleting : text.delete}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-issue-report-detail-dialog">
          <DialogHeader>
            <DialogTitle>{language === "es" ? "Detalle del reporte" : "Issue report detail"}</DialogTitle>
            <DialogDescription>
              {selectedReport ? `${selectedReport.user_username} • ${formatDate(selectedReport.created_at)}` : ""}
            </DialogDescription>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-2xl border border-border bg-muted/20 p-4 text-sm sm:grid-cols-2">
                <div data-testid="admin-issue-report-detail-user"><span className="font-medium">{language === "es" ? "Usuario" : "User"}:</span> {selectedReport.user_username}</div>
                <div data-testid="admin-issue-report-detail-email"><span className="font-medium">Email:</span> {selectedReport.user_email}</div>
                <div data-testid="admin-issue-report-detail-path"><span className="font-medium">{language === "es" ? "Ruta" : "Path"}:</span> {selectedReport.page_path || "—"}</div>
                <div data-testid="admin-issue-report-detail-video"><span className="font-medium">{language === "es" ? "Video" : "Video"}:</span> {selectedReport.video_filename || selectedReport.video_id || "—"}</div>
              </div>

              <div className="rounded-2xl border border-border p-4" data-testid="admin-issue-report-detail-message">
                <p className="mb-2 text-sm font-medium">{language === "es" ? "Mensaje completo" : "Full message"}</p>
                <p className="whitespace-pre-wrap text-sm leading-7 text-foreground">{selectedReport.message}</p>
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setReportToDelete(selectedReport)}
                  disabled={deletingReportId === selectedReport.report_id}
                  data-testid="admin-issue-report-detail-delete-button"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {deletingReportId === selectedReport.report_id ? text.deleting : text.delete}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!reportToDelete} onOpenChange={(open) => !open && setReportToDelete(null)}>
        <AlertDialogContent data-testid="admin-issue-report-delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{text.deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {text.deleteDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-issue-report-delete-cancel-button">{text.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="admin-issue-report-delete-confirm-button"
            >
              {deletingReportId === reportToDelete?.report_id ? text.deleting : text.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};